import React, { createContext, useState, useEffect, useContext } from "react";
import axios from "axios";
import Moment from "moment";

import { AuthContext } from "./AuthContext";
import { ScreenContext } from "./ScreenContext";

export const HoursContext = createContext();

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:8080/";

const HoursContextProvider = (props) => {
  const { user, token, logout } = useContext(AuthContext);
  const { setSnackbarMessage } = useContext(ScreenContext);

  const [date, setDate] = useState(Moment().format("YYYY-MM-DD"));
  const [hours, setHours] = useState([]);
  const [hoursSummary, setHoursSummary] = useState([]);
  const [totalHours, setTotalHours] = useState(0);
  const [selectedDays, setSelectedDays] = useState([]);
  const [jobDetails, setJobDetails] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [togglQuotaStatus, setTogglQuotaStatus] = useState(null);

  const changeDate = (inputDate) => {
    // When user picks a date from calendar, extract ONLY the date components (year, month, day)
    // Ignore timezone, time, and UTC offset completely - Oct 31 is Oct 31 everywhere
    let year, month, day;
    
    if (Moment.isMoment(inputDate)) {
      // Date picker with MomentUtils passes a Moment object
      year = inputDate.year();
      month = inputDate.month() + 1; // Moment months are 0-indexed
      day = inputDate.date();
    } else if (inputDate instanceof Date) {
      // JavaScript Date object - use local date components
      year = inputDate.getFullYear();
      month = inputDate.getMonth() + 1; // JS months are 0-indexed
      day = inputDate.getDate();
    } else if (typeof inputDate === 'string') {
      // Already a string like "2025-10-31"
      setDate(inputDate);
      return;
    } else {
      // Fallback - parse with Moment
      const m = Moment(inputDate);
      year = m.year();
      month = m.month() + 1;
      day = m.date();
    }
    
    // Format as YYYY-MM-DD with zero-padding
    const formattedDate = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    setDate(formattedDate);
  };

  // Function to check Toggl quota status
  const checkTogglQuota = async (forceRefresh = false) => {
    try {
      // Add timestamp to prevent caching when forcing refresh
      const url = forceRefresh ? 
        `${API_URL}toggl/quota?_=${Date.now()}` : 
        `${API_URL}toggl/quota`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 5000
      });
      
      setTogglQuotaStatus(response.data);
      return response.data;
    } catch (error) {
      console.warn("Could not fetch Toggl quota status:", error.message);
      return null;
    }
  };

  const toggl = async (inputDate) => {
    if (!inputDate || inputDate === "") return;

    // Check quota status before attempting import
    const quotaStatus = await checkTogglQuota();
    
    if (quotaStatus && quotaStatus.quota.remaining === 0) {
      const waitMinutes = Math.ceil(quotaStatus.quota.resetInSeconds / 60);
      setSnackbarMessage({
        message: `Toggl API limit reached (${quotaStatus.quota.used}/${quotaStatus.quota.limit} requests used). Please wait ${waitMinutes} minutes before importing again.`,
        severity: "warning",
      });
      return;
    }
    
    if (quotaStatus && quotaStatus.quota.remaining <= 2) {
      setSnackbarMessage({
        message: `Warning: Only ${quotaStatus.quota.remaining} Toggl requests remaining this hour. Use wisely!`,
        severity: "warning",
      });
    }

    setLoading(true);

    let source = axios.CancelToken.source();

    // Detect user's timezone
    const userTimezone = Moment.tz.guess();

    axios
      .post(
        API_URL + "toggl",
        {
          date: Moment(inputDate).format("YYYY-MM-DD"),
          who: user.username,
          timezone: userTimezone, // Send user's timezone to backend
        },
        {
          cancelToken: source.token,
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {
        setLoading(false);
        loadHours();
        
        // Force refresh quota status after successful import
        checkTogglQuota(true);
        
        setSnackbarMessage({
          message: `${Moment(date).format(
            "MM-DD-YYYY"
          )} Toggl entries imported successfully.`,
          severity: "success",
        });
      })
      .catch((error) => {
        setLoading(false);
        
        if (error.response) {
          // Handle different error types with better user messages
          let errorMessage = `Error ${error.response.status}`;
          
          switch (error.response.status) {
            case 401:
              errorMessage = "Authentication failed. Please check your login and Toggl API key.";
              break;
            case 400:
              errorMessage = error.response.data || "Invalid request data.";
              break;
            case 404:
              errorMessage = "Toggl API endpoint not found.";
              break;
            case 429:
              // Handle quota exceeded with helpful message
              if (error.response.data && error.response.data.message === 'quota_exceeded') {
                const quotaInfo = error.response.data.quotaInfo;
                const resetMinutes = Math.ceil(quotaInfo.resetInSeconds / 60);
                errorMessage = `Toggl API limit reached (${quotaInfo.used}/${quotaInfo.limit} requests used). Please wait ${resetMinutes} minutes or upgrade your Toggl plan.`;
              } else {
                errorMessage = "Too many requests. Please wait and try again.";
              }
              break;
            case 500:
              errorMessage = "Server error occurred while importing Toggl data.";
              break;
            default:
              if (error.response.data && typeof error.response.data === 'string') {
                errorMessage = error.response.data;
              } else if (error.response.data && error.response.data.error) {
                errorMessage = error.response.data.error;
              }
          }
          
          setSnackbarMessage({
            message: errorMessage,
            severity: "error",
          });
        } else if (error.request) {
          setSnackbarMessage({
            message: "Network error: Could not connect to server.",
            severity: "error",
          });
        } else {
          setSnackbarMessage({
            message: "An unexpected error occurred.",
            severity: "error",
          });
        }
        
        // Always force refresh quota status after any API call (success or error)
        checkTogglQuota(true);
      });

    //Cancel axios request when component unmounted
    return () => {
      // setLoading(false);
      source.cancel();
    };
  };

  const emailEntries = (inputDate) => {
    if (!inputDate || inputDate === "") return;

    setLoading(true);

    let source = axios.CancelToken.source();

    axios
      .post(
        API_URL + "email-hours",
        {
          date: Moment(inputDate).format("YYYY-MM-DD"),
          who: user.username,
        },
        {
          cancelToken: source.token,
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {

        setLoading(false);

        setSnackbarMessage({
          message: `${Moment(inputDate).format(
            "MM-DD-YYYY"
          )} Hours sent successfully.`,
          severity: "success",
        });
      })
      .catch((error) => {
        if (error.response) {


          setSnackbarMessage({
            message: error.response.status,
            severity: "error",
          });
        }
        setLoading(false);
      });

    //Cancel axios request when component unmounted
    return () => {
      // setLoading(false);
      source.cancel();
    };
  };

  const createTicket = (selectedItem) => {
    if (!selectedItem || selectedItem === null) return;

    setLoading(true);

    let source = axios.CancelToken.source();

    axios
      .post(
        API_URL + "create-ticket",
        {
          selectedItem,
        },
        {
          cancelToken: source.token,
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {

        setLoading(false);

        setSnackbarMessage({
          message: "Hour entry sent to Zoho successfully.",
          severity: "success",
        });

        setJobDetails({ key: Math.random() }); // { key: Math.random() } forces JobDetails state to refresh
        loadHours();
      })
      .catch((error) => {
        if (error.response) {


          setSnackbarMessage({
            message: error.response.status,
            severity: "error",
          });
        }
        setLoading(false);
      });

    //Cancel axios request when component unmounted
    return () => {
      // setLoading(false);
      source.cancel();
    };
  };

  const deleteHour = (selectedItem) => {
    if (!selectedItem || selectedItem === null) return;

    let CombinedID = `${selectedItem.ID}|${Moment(
      selectedItem.Completed
    ).format("YYYY-MM-DD")}|${selectedItem.StartTime}|${user.username}`;

    setLoading(true);

    let source = axios.CancelToken.source();

    axios
      .post(
        API_URL + "delete-hour",
        {
          CombinedID,
        },
        {
          cancelToken: source.token,
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {

        setLoading(false);

        setSnackbarMessage({
          message: "Hour entry has been deleted.",
          severity: "success",
        });

        setJobDetails({ key: Math.random() }); // { key: Math.random() } forces JobDetails state to refresh
        loadHours();
      })
      .catch((error) => {
        if (error.response) {


          setSnackbarMessage({
            message: error.response.status,
            severity: "error",
          });
        }
        setLoading(false);
      });

    //Cancel axios request when component unmounted
    return () => {
      // setLoading(false);
      source.cancel();
    };
  };

  const saveHour = (newHour) => {
    if (!newHour || newHour.length === 0) return;

    setLoading(true);
    let source = axios.CancelToken.source();

    axios
      .post(
        API_URL + "save-hour",
        {
          newHour: newHour,
        },
        {
          cancelToken: source.token,
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {



        setLoading(false);
        setJobDetails(null);
        setSnackbarMessage({
          message: "Hour entry has been saved.",
          severity: "success",
        });
        loadHours();
      })
      .catch((error) => {
        if (error.response) {



          // logout();
          setSnackbarMessage({
            message: error.response.status,
            severity: "error",
          });
        }
        setLoading(false);

      });


    //Cancel axios request when component unmounted
    return () => {
      // setLoading(false);
      source.cancel();
    };
  };

  const loadHours = () => {
    setLoading(true);

    let source = axios.CancelToken.source();

    axios
      .post(
        API_URL + "get-hours",
        {
          // date: Moment(date).utc().format("YYYY-MM-DD"),
          date: date,
          who: user.username,
        },
        {
          cancelToken: source.token,
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {




        let reshours = res.data.hours;
        let resdays = res.data.days;

        var calculateTotalHours = reshours.reduce(function (prev, cur) {
          return prev + cur.Qty;
        }, 0);

        setHours(reshours);
        setTotalHours(calculateTotalHours);

        if (resdays.length !== 0) {
          let array = resdays.split(",").map(Number);
          setSelectedDays(array);
        } else {
          setSelectedDays([]);
        }

        setLoading(false);
      })
      .catch((error) => {
        if (error.response) {



          logout();
        }
        setLoading(false);

      });

    //Cancel axios request when component unmounted
    return () => {
      // setLoading(false);
      source.cancel();
    };
  };

  const getHoursSummary = () => {
    setLoading(true);

    let source = axios.CancelToken.source();

    axios
      .post(
        API_URL + "get-hours-summary",
        {
          // date: Moment(date).utc().format("YYYY-MM-DD"),
          date: date,
          who: user.username,
        },
        {
          cancelToken: source.token,
          headers: { Authorization: `Bearer ${token}` },
        }
      )
      .then((res) => {



        setHoursSummary(res.data.hourssummary[0]);


        setLoading(false);
      })
      .catch((error) => {
        if (error.response) {

          logout();
        }
        setLoading(false);

      });

    //Cancel axios request when component unmounted
    return () => {
      // setLoading(false);
      source.cancel();
    };
  };

  return (
    <HoursContext.Provider
      value={{
        hours,
        setHours,
        totalHours,
        setTotalHours,
        date,
        changeDate,
        selectedDays,
        jobDetails,
        setJobDetails,
        loadHours,
        getHoursSummary,
        hoursSummary,
        saveHour,
        deleteHour,
        createTicket,
        emailEntries,
        toggl,
        isLoading,
        togglQuotaStatus,
        checkTogglQuota,
      }}
    >
      {props.children}
    </HoursContext.Provider>
  );
};

export default HoursContextProvider;
